import React from 'react'
import { List, useTable, EditButton } from '@refinedev/antd'
import { useUpdate } from '@refinedev/core'
import { Table, Space, Switch } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { Settings, ShieldCheck } from '../../components/icons'
import { isOnboardingOwned } from '../onboarding_settings/page'
import { isDemoOwned } from '../demo_settings/page'
import { isJaguarOwnedOwned } from '../jaguar_owned_controls/page'
import { isGroupsOwned } from '../groups_settings/page'

interface FeatureFlag {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

/**
 * Feature flags / remote config. The inline Switch flips a flag immediately
 * (useUpdate) — combined with the table's Realtime publication, the mobile app
 * reflects the change in ~real time (kill-switches, soft launches, maintenance).
 *
 * Trust & Safety flags (the `integrity_*` abuse controls + the standalone
 * `dm_approval_required` privacy control) are EXCLUDED here because they are
 * managed on the dedicated Safety Controls page (/integrity_settings). This
 * keeps each flag's on/off switch in exactly ONE place — no toggling the same
 * feature from two different tabs. The predicate mirrors what Safety Controls
 * surfaces, so any future `integrity_*` flag is automatically owned there.
 */
const isSafetyOwned = (key: string): boolean =>
  key.startsWith('integrity_') || key === 'dm_approval_required'

/**
 * Classrooms flags (today only `classrooms_memories_enabled`, the Memories
 * switch) belong to the Classrooms tab (Community › Groups › Classrooms,
 * /group_chapters), which asks before turning Memories on. Prefix-matched like
 * isGroupsOwned, so a future classrooms_* flag is hidden here automatically.
 * Defined here rather than imported from group_chapters/ so this page's
 * exclusion list doesn't depend on that page's module.
 */
export const isClassroomsOwned = (key: string): boolean => key.startsWith('classrooms_')

export function FeatureFlagList() {
  // feature_flags is a small, domain-bounded table (a fixed set of remote-config
  // keys), so an explicit bounded pageSize is enough to never block the UI — no
  // summary view needed. We keep all flags on one page (200 >> the real count)
  // so the client-side ownership filtering + deck counts stay correct.
  const { tableProps } = useTable<FeatureFlag>({
    resource: 'feature_flags',
    syncWithLocation: true,
    pagination: { pageSize: 200 },
    meta: { idColumnName: 'key' },
  })
  const { mutate: update } = useUpdate()

  // Drop the Safety-Controls-, Onboarding-, Demo-Mode-, Jaguar-Owned-,
  // Groups-Settings- and Classrooms-owned flags so they can't also be toggled
  // here (each flag has exactly one management surface).
  const flags = ((tableProps.dataSource ?? []) as FeatureFlag[]).filter(
    (f) =>
      !isSafetyOwned(f.key) &&
      !isOnboardingOwned(f.key) &&
      !isDemoOwned(f.key) &&
      !isJaguarOwnedOwned(f.key) &&
      !isGroupsOwned(f.key) &&
      !isClassroomsOwned(f.key),
  )
  const enabledCount = flags.filter((f) => f.enabled).length

  const stats: Stat[] = [
    { label: 'Total Flags', value: flags.length.toLocaleString(), icon: Settings, tone: 'jaguar' },
    { label: 'Enabled', value: enabledCount.toLocaleString(), icon: ShieldCheck, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'System · Feature Flags',
        description:
          'Remote config switches that change the mobile app in real time — kill-switches, soft launches, and maintenance mode. Flip a flag and members feel it within seconds. Trust & Safety controls live on the Safety Controls page (Trust & Safety → Safety Controls), so they are not listed here. Jaguar-Owned switches live on Jaguar-Owned controls (Community → Jaguar-Owned controls), so they are not listed here either. Groups switches live on Group Settings (Community → Groups → Group Settings), and the Memories switch lives on Classrooms (Community → Groups → Classrooms), so neither is listed here.',
      }}
      stats={stats}
      statColumns={2}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} dataSource={flags} rowKey="key">
            <Table.Column
              dataIndex="enabled"
              title="Enabled"
              render={(value: boolean, record: FeatureFlag) => (
                <Switch
                  checked={value}
                  onChange={(checked) =>
                    update({
                      resource: 'feature_flags',
                      id: record.key,
                      values: { enabled: checked },
                      meta: { idColumnName: 'key' },
                    })
                  }
                />
              )}
            />
            <Table.Column
              dataIndex="key"
              title="Key"
              render={(value: string) => <Pill tone="gold">{value}</Pill>}
            />
            <Table.Column
              dataIndex="description"
              title="Description"
              render={(value: string | null) =>
                value ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, color: brand.sub }}>{value}</span>
                ) : (
                  '—'
                )
              }
            />
            <Table.Column
              dataIndex="updated_at"
              title="Updated"
              render={(value: string) => (value ? new Date(value).toLocaleString() : '—')}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              render={(_, record: FeatureFlag) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.key} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
